/**
 * Application Configuration
 * All configurable values should be defined here
 */

export const APP_CONFIG = {
  // Application metadata
  APP_NAME: 'Immobilien-Analyzer',
  APP_VERSION: '2.0.0',
  
  // Regional settings
  REGION: {
    NAME: 'Bayern',
    CURRENCY: 'EUR',
    LOCALE: 'de-DE',
    DATE_FORMAT: 'DD.MM.YYYY',
  },
  
  // Tax configuration
  TAX: {
    INCOME_TAX_RATE: 0.42, // 42% Spitzensteuersatz
    SOLIDARITY_SURCHARGE: 0.055, // 5.5% Solidaritätszuschlag
    CHURCH_TAX: 0.08, // 8% Kirchensteuer (Bayern)
    PROPERTY_TRANSFER_TAX: 0.035, // 3.5% Grunderwerbsteuer Bayern
    NOTARY_FEES: 0.015, // 1.5% Notar- und Grundbuchkosten
    DEPRECIATION_RATE: 0.02, // 2% AfA for residential buildings
    DEPRECIATION_YEARS: 50, // 50 years for buildings built after 1925
  },
  
  // Financial defaults
  FINANCE: {
    DEFAULT_INTEREST_RATE: 3.5,
    DEFAULT_REPAYMENT_RATE: 2.0,
    DEFAULT_FINANCING_PERCENTAGE: 80,
    MIN_FINANCING_PERCENTAGE: 0,
    MAX_FINANCING_PERCENTAGE: 110,
    DEFAULT_PROJECTION_YEARS: 30,
    MAX_PROJECTION_YEARS: 40,
  },
  
  // Property defaults
  PROPERTY: {
    DEFAULT_PRICE: 320000,
    MIN_PRICE: 10000,
    MAX_PRICE: 10000000,
    DEFAULT_SIZE: 75,
    MIN_SIZE: 10,
    MAX_SIZE: 1000,
    DEFAULT_RENT_PER_SQM: 12,
    MIN_RENT_PER_SQM: 1,
    MAX_RENT_PER_SQM: 50,
  },
  
  // Cost defaults
  COSTS: {
    DEFAULT_HAUSGELD: 200,
    DEFAULT_MAINTENANCE: 20,
    DEFAULT_ADMINISTRATION: 20,
    DEFAULT_VACANCY_RATE: 0.02, // 2% vacancy rate
    DEFAULT_RENT_INCREASE_RATE: 0.02, // 2% annual rent increase
    DEFAULT_VALUE_APPRECIATION: 0.025, // 2.5% annual value appreciation
  },
  
  // UI Configuration
  UI: {
    CHART_COLORS: {
      PRIMARY: '#3B82F6',
      SUCCESS: '#10B981',
      DANGER: '#EF4444',
      WARNING: '#F59E0B',
      INFO: '#6366F1',
    },
    ANIMATION_DURATION: 300,
    DEBOUNCE_DELAY: 500,
    MAX_PORTFOLIO_SIZE: 100,
    MAX_COMPARISON_PROPERTIES: 5,
  },
  
  // Storage configuration
  STORAGE: {
    LOCAL_STORAGE_PREFIX: 'immobilien_analyzer_',
    LOCAL_STORAGE_VERSION: '2.0.0',
    MAX_STORAGE_SIZE: 5 * 1024 * 1024, // 5MB
    ENABLE_ENCRYPTION: false, // Set to true for production
  },
  
  // CSV Export/Import
  CSV: {
    DELIMITER: ';', // German CSV standard
    LINE_BREAK: '\r\n',
    ENCODING: 'UTF-8',
    USE_BOM: true, // For Excel compatibility
  },
  
  // PDF Export
  PDF: {
    PAGE_FORMAT: 'A4',
    ORIENTATION: 'portrait',
    MARGIN_TOP: 20,
    MARGIN_BOTTOM: 20,
    MARGIN_LEFT: 15,
    MARGIN_RIGHT: 15,
    FONT_SIZE: 11,
    HEADER_FONT_SIZE: 14,
  },
  
  // Validation rules
  VALIDATION: {
    ENABLE_STRICT: true,
    ALLOW_NEGATIVE_VALUES: false,
    REQUIRE_ALL_FIELDS: false,
    MAX_INPUT_LENGTH: 100,
  },
  
  // Performance
  PERFORMANCE: {
    USE_WEB_WORKERS: false, // Enable for heavy calculations
    CACHE_CALCULATIONS: true,
    CACHE_DURATION: 5 * 60 * 1000, // 5 minutes
    DEBOUNCE_CALCULATIONS: true,
  },
  
  // Feature flags
  FEATURES: {
    ENABLE_DARK_MODE: false,
    ENABLE_CLOUD_SYNC: false,
    ENABLE_AI_RECOMMENDATIONS: false,
    ENABLE_MARKET_DATA: false,
    ENABLE_MULTI_LANGUAGE: false,
    ENABLE_EXPORT_EXCEL: false,
    ENABLE_UNDO_REDO: false,
  },
  
  // API endpoints (for future use)
  API: {
    BASE_URL: process.env.REACT_APP_API_URL || '',
    TIMEOUT: 30000,
    RETRY_COUNT: 3,
  },
  
  // Error messages
  ERRORS: {
    DIVISION_BY_ZERO: 'Division durch Null nicht möglich',
    INVALID_INPUT: 'Ungültige Eingabe',
    STORAGE_FULL: 'Speicher ist voll',
    IMPORT_FAILED: 'Import fehlgeschlagen',
    EXPORT_FAILED: 'Export fehlgeschlagen',
    CALCULATION_ERROR: 'Berechnungsfehler',
  },
};

// Type-safe config getter
export function getConfig<T extends keyof typeof APP_CONFIG>(key: T): typeof APP_CONFIG[T] {
  return APP_CONFIG[key];
}

// Environment-specific overrides
if (process.env.NODE_ENV === 'development') {
  APP_CONFIG.VALIDATION.ENABLE_STRICT = false;
  APP_CONFIG.PERFORMANCE.USE_WEB_WORKERS = false;
}

export default APP_CONFIG;